import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  limit,
  startAfter,
  getCountFromServer,
  writeBatch,
  setDoc,
} from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';

@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private firestore: Firestore = inject(Firestore);
  private storage: Storage = inject(Storage);
  private currentPath: string = '';

  setPath(path: string): void {
    this.currentPath = path;
  }

  async loadPage(path: string, pageSize: number, lastDoc?: any): Promise<{ data: any[]; lastDoc: any; hasMore: boolean }> {
  const colRef = collection(this.firestore, path);
  
  // 🔥 Fetch one extra item to check if there are more pages
  let q = query(colRef, limit(pageSize + 1));
  
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snapshot = await getDocs(q);
  const allDocs = snapshot.docs;
  
  // Only return pageSize items, but check if there's an extra
  const hasMore = allDocs.length > pageSize;
  const docsToShow = allDocs.slice(0, pageSize);
  
  const data = docsToShow.map((docSnap) => ({ 
    id: docSnap.id, 
    ...(docSnap.data() as Record<string, any>) 
  }));
  
  const lastDocument = docsToShow.length > 0 ? docsToShow[docsToShow.length - 1] : null;
  
  return { 
    data, 
    lastDoc: lastDocument, 
    hasMore  // ✅ Return hasMore!
  };
}

  async getTotal(path: string): Promise<number> {
    const colRef = collection(this.firestore, path);
    const snapshot = await getCountFromServer(colRef);
    return snapshot.data().count;
  }

  // CHANGE: transform before add; also set currentPath from the provided path for consistency
  async add(path: string, item: any): Promise<void> {
  this.setPath(path);
  const colRef = collection(this.firestore, path);

  const transformedItem = this.transformData(item);   // your permissions logic
  const newDocRef = doc(colRef);                     // ← auto-id generated here
  const id = newDocRef.id;                           // ← grab the id

  await setDoc(newDocRef, { ...transformedItem, id }); // inject id into the doc
}

  // CHANGE: transform before update
  async update(id: string, data: any): Promise<void> {
    const docRef = doc(this.firestore, this.currentPath, id);
    const transformedData = this.transformData(data);
    await updateDoc(docRef, transformedData);
  }

  async delete(id: string): Promise<void> {
    const docRef = doc(this.firestore, this.currentPath, id);
    await deleteDoc(docRef);
  }

  async deleteBatch(ids: string[]): Promise<void> {
    const batch = writeBatch(this.firestore);
    ids.forEach((id) => {
      const docRef = doc(this.firestore, this.currentPath, id);  
      batch.delete(docRef);
    });
    await batch.commit();
  }

  async deleteAll(): Promise<void> {
    const colRef = collection(this.firestore, this.currentPath);  
    const snapshot = await getDocs(colRef);
    const batch = writeBatch(this.firestore);
    snapshot.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  async uploadFile(path: string, file: File): Promise<string> {
    const storageRef = ref(this.storage, `${path}/${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  }

  // CHANGE: transform each item before adding to the batch
  async importData(items: any[]): Promise<void> {
    const colRef = collection(this.firestore, this.currentPath);  
    const batch = writeBatch(this.firestore);
    items.forEach((item) => {
      const transformedItem = this.transformData(item);
      const docRef = doc(colRef);
      batch.set(docRef, transformedItem);
    });
    await batch.commit();
  }

  // NEW: same permissions transformation you used in the demo
  private transformData(item: any): any {
    const transformedItem = { ...item };

    if (transformedItem.permissions) {
      // Ensure it's an object
      if (typeof transformedItem.permissions !== 'object' || Array.isArray(transformedItem.permissions)) {
        transformedItem.permissions = {};
      }

      // Case 1: selected/unselected arrays -> map of show/hide
      if (transformedItem.permissions.selected || transformedItem.permissions.unselected) {
        const selected = transformedItem.permissions.selected || [];
        const unselected = transformedItem.permissions.unselected || [];
        const permissionsMap: Record<string, string> = {};
        selected.forEach((perm: string) => { permissionsMap[perm] = 'show'; });
        unselected.forEach((perm: string) => { permissionsMap[perm] = 'hide'; });
        transformedItem.permissions = permissionsMap;
      }

      // Case 2: fullState boolean map -> map of show/hide
      if (transformedItem.permissions.fullState) {
        const fullState = transformedItem.permissions.fullState;
        const permissionsMap: Record<string, string> = {};
        Object.keys(fullState).forEach((key) => {
          permissionsMap[key] = fullState[key] ? 'show' : 'hide';
        });
        transformedItem.permissions = permissionsMap;
      }
    }

    return transformedItem;
  }
}